import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { recipeAPI } from '../services/api';

function AISearchModal({ boards = [], onClose, onImportComplete }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recipes, setRecipes] = useState([]);
  const [webResults, setWebResults] = useState([]);
  const [addToListFor, setAddToListFor] = useState(null);
  const [addToListListId, setAddToListListId] = useState('');
  const [addingToList, setAddingToList] = useState(false);

  const lists = boards.flatMap((b) => (b.lists || []).map((lst) => ({ ...lst, boardTitle: b.title })));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = (query || '').trim();
    if (!q) {
      setError('Please enter a question or search (e.g. "KTF coleslaw" or "best potato salad recipe").');
      return;
    }
    setError('');
    setLoading(true);
    setRecipes([]);
    setWebResults([]);
    setAddToListFor(null);
    try {
      const response = await recipeAPI.aiSearch(q);
      setRecipes(response.data.recipes || []);
      setWebResults(response.data.web_results || []);
      if (
        !(response.data.recipes || []).length &&
        !(response.data.web_results || []).length &&
        !response.data.error
      ) {
        setError('No matching recipes found. Try different words or add more recipes.');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Search failed. Please try again.');
      setRecipes([]);
      setWebResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (recipe) => {
    onClose();
    navigate(`/recipe/${recipe.id}`);
  };

  const handleAddToListClick = (webResult) => {
    setAddToListFor({ url: webResult.url, title: webResult.title });
    setAddToListListId(lists.length ? String(lists[0].id) : '');
  };

  const handleAddToListSubmit = async () => {
    if (!addToListFor || !addToListListId) return;
    setAddingToList(true);
    try {
      const res = await recipeAPI.importFromUrl(addToListFor.url, parseInt(addToListListId, 10));
      if (res.data?.recipe?.id) {
        onImportComplete?.();
        onClose();
        navigate(`/recipe/${res.data.recipe.id}`);
      }
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to import recipe from link.');
    } finally {
      setAddingToList(false);
      setAddToListFor(null);
    }
  };

  const hasResults = recipes.length > 0 || webResults.length > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal ai-search-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Ask AI for recipes</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="ai-query">What are you looking for?</label>
            <input
              id="ai-query"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder='e.g. "KTF coleslaw" or "best potato salad recipe"'
              disabled={loading}
              autoFocus
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <div className="modal-actions" style={{ marginBottom: '1rem' }}>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? (
                <>
                  <span
                    className="loading-spinner"
                    style={{
                      width: '18px',
                      height: '18px',
                      borderWidth: '2px',
                      marginRight: '6px',
                      verticalAlign: 'middle',
                    }}
                  />
                  Searching…
                </>
              ) : (
                'Search my recipes & web'
              )}
            </button>
          </div>
        </form>

        {hasResults && (
          <div className="ai-search-results">
            {recipes.length > 0 && (
              <>
                <div className="ai-search-results-header">Your recipes — click to open</div>
                <ul className="ai-search-results-list">
                  {recipes.map((recipe) => (
                    <li key={recipe.id}>
                      <button
                        type="button"
                        className="ai-search-result-item"
                        onClick={() => handleResultClick(recipe)}
                      >
                        <span className="ai-search-result-title">{recipe.title}</span>
                        {recipe.description && (
                          <span className="ai-search-result-desc">{recipe.description}</span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {webResults.length > 0 && (
              <>
                <div className="ai-search-results-header" style={{ marginTop: recipes.length > 0 ? '1rem' : 0 }}>
                  From the web — add to your recipes or open link
                </div>
                <ul className="ai-search-results-list ai-search-web-list">
                  {webResults.map((wr, idx) => (
                    <li key={idx} className="ai-search-web-item">
                      <div className="ai-search-web-title">
                        <a
                          href={wr.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ai-search-web-link"
                        >
                          {wr.title}
                        </a>
                      </div>
                      {wr.snippet && (
                        <p className="ai-search-web-snippet">{wr.snippet}</p>
                      )}
                      <button
                        type="button"
                        className="btn btn-primary btn-sm ai-search-add-btn"
                        onClick={() => handleAddToListClick(wr)}
                        disabled={!!addToListFor}
                      >
                        Add to my recipes
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            {addToListFor && (
              <div className="ai-search-add-to-list-bar">
                <span className="ai-search-add-to-list-label">
                  Add &quot;{addToListFor.title.slice(0, 40)}
                  {addToListFor.title.length > 40 ? '…' : ''}&quot; to:
                </span>
                <select
                  value={addToListListId}
                  onChange={(e) => setAddToListListId(e.target.value)}
                  disabled={addingToList}
                  className="ai-search-add-select"
                >
                  {lists.map((lst) => (
                    <option key={lst.id} value={lst.id}>
                      {lst.boardTitle} → {lst.title}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleAddToListSubmit}
                  disabled={addingToList || !addToListListId}
                >
                  {addingToList ? (
                    <>
                      <span
                        className="loading-spinner"
                        style={{ width: '16px', height: '16px', borderWidth: '2px', marginRight: '4px', verticalAlign: 'middle' }}
                      />
                      Importing…
                    </>
                  ) : (
                    'Import'
                  )}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setAddToListFor(null)}
                  disabled={addingToList}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default AISearchModal;
